import ClearIcon from "@mui/icons-material/ClearRounded";
import { Button, IconButton, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { OrganizationMemberInvitation } from "../../../../../api/types/userManagementTypes";
import MembersContext from "../MembersContext";
import { getDefaultGridSx } from "../definitions";
import { InvitationStatusCell, MemberRoleCell } from "./membersGridDefinitions";

const getPendingInvitesGridColumns = (): GridColDef<OrganizationMemberInvitation>[] => [
  {
    field: "email",
    headerName: "Email",
    flex: 1.5,
    renderCell: ({ row }) => row.email,
  },
  {
    field: "isGuest",
    headerName: "Type",
    flex: 1,
    valueFormatter: (value) => (value ? "External" : "Internal"),
  },
  {
    field: "organizationRole",
    headerName: "Role",
    flex: 1,
    renderCell: ({ row }) => <MemberRoleCell roles={[row.organizationRole]} />,
  },
  {
    field: "invitedAt",
    headerName: "Invited at",
    flex: 1,
    valueFormatter: (value) => (value ? convertISODateTime(value) : ""),
  },
  {
    field: "invitationStatus",
    headerName: "Status",
    flex: 1,
    renderCell: ({ row }) => <InvitationStatusCell status={row.invitationStatus} />,
  },
];

const getRowId = (row: OrganizationMemberInvitation) => row.invitationId;

const PendingInvitesGrid = () => {
  const { filteredPendingInvites, isLoading, hasAccessToManageOrganizationUsers, deleteInvite, resendInvite } =
    useDefinedContext(MembersContext);

  const columns = getPendingInvitesGridColumns();

  if (hasAccessToManageOrganizationUsers) {
    columns.push({
      field: "resend",
      headerName: "",
      width: 130,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          onClick={() => resendInvite(row)}
          disabled={row.invitationStatus === "PendingApproval" || row.invitationStatus === "Rejected"}
        >
          Resend invite
        </Button>
      ),
    });
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 72,
      cellClassName: "grid-row-actions",
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <Tooltip placement="top-start" title="Invalidate invite" arrow>
          <IconButton onClick={() => deleteInvite(row)} color="error">
            <ClearIcon />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  return (
    <DataGrid<OrganizationMemberInvitation>
      getRowId={getRowId}
      sx={(theme) => getDefaultGridSx(theme)}
      columns={columns}
      rows={filteredPendingInvites}
      loading={isLoading}
      multilineRows
      noRowsText="No pending invites"
      disableColumnSorting
    />
  );
};

export default PendingInvitesGrid;
